import { useState } from 'react';
import type { SqlImportReply } from '@/services/db/sql-import/mutations';
import type { SqlImportDraft } from './draft';
import { isEmptyString } from '@/utils/string';
import { useBaselinedDraft } from './use-baselined-draft';

/**
 * The wizard's draft under edit, the baseline it is compared against (as last loaded or saved),
 * and the id of the saved import it belongs to once it has one.
 */
export function useSqlImportDraft(initialDraft: SqlImportDraft, draftId: string | null) {
  const { draft, currentDraft, differsFromBaseline, setDraftFields, resetToBaseline, rebaseline } =
    useBaselinedDraft(initialDraft);
  const [savedId, setSavedId] = useState<string | null>(draftId);

  /** The save that landed carried `sentDraft`; edits typed while it was in flight stay unsaved. */
  const markSaved = (reply: SqlImportReply, sentDraft: SqlImportDraft) => {
    setSavedId(reply.sql_import.id);
    rebaseline(sentDraft);
  };

  // A draft with no name is nothing to save, so it never counts as dirty.
  const hasUnsavedChanges = () => differsFromBaseline() && !isEmptyString(currentDraft().name);

  return {
    draft,
    savedId,
    isDirty: hasUnsavedChanges(),
    hasUnsavedChanges,
    setDraftFields,
    resetToBaseline,
    markSaved,
  };
}
